import React, { useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  Outlet,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import {
  LocalizationProvider,
  useLocalization,
} from "./contexts/LocalizationContext";
import { ChatProvider } from "./contexts/ChatContext";

// Firebase
import './config/firebase';
import analyticsService from './services/AnalyticsService';
import usePageTracking from './hooks/usePageTracking';
import useUtmParams from './hooks/useUtmParams';
import PushNotificationService from './services/PushNotificationService';

// Screens
import LoginScreen from "./screens/Auth/LoginScreen";
import RegisterScreen from "./screens/Auth/RegisterScreen";
import EmailVerificationScreen from "./screens/Auth/EmailVerificationScreen";
import ForgotPasswordScreen from "./screens/Auth/ForgotPasswordScreen";
import ResetPasswordScreen from "./screens/Auth/ResetPasswordScreen";
import ChatScreen from "./screens/Chat/ChatScreen";
import ProfileScreen from "./screens/Profile/ProfileScreen";
import SettingsScreen from "./screens/Settings/SettingsScreen";
import FAQScreen from "./screens/FAQ/FAQScreen";
import BlogScreen from "./screens/Blog/Blog";
import ArchiveScreen from "./screens/Archive/ArchiveScreen";
import SubscriptionScreen from "./screens/Subscription/SubscriptionScreen";
import SharedChatScreen from "./screens/Shared/SharedChatScreen";

// Components
import Sidebar from "./components/Layout/SideBar";
import Loading from "./components/UI/Loading";
import Header from "./components/Layout/Header";

// Global styles
import "./styles/globals.css";
import "./styles/themes.css";

// Language Selector Component
const LanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentLanguage, changeLanguage, changeLanguageLocal } =
    useLocalization();
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const deviceType = useDeviceType();
  // Get token from localStorage (or context if available)
  const token = localStorage.getItem("token");

  const flagCdnBase = "https://flagcdn.com";
  const languages = [
    {
      code: "uk",
      name: "Українська",
      iconUrl: `${flagCdnBase}/ua.svg`,
    },
    {
      code: "en",
      name: "English",
      iconUrl: `${flagCdnBase}/us.svg`,
    },
    {
      code: "pl",
      name: "Polski",
      iconUrl: `${flagCdnBase}/pl.svg`,
    },
    {
      code: "ru",
      name: "Русский",
      iconUrl: `${flagCdnBase}/ru.svg`,
    },
    {
      code: "es",
      name: "Español",
      iconUrl: `${flagCdnBase}/es.svg`,
    },
    {
      code: "pt",
      name: "Português",
      iconUrl: `${flagCdnBase}/pt.svg`,
    },
  ];
  const currentLang = languages.find((lang) => lang.code === currentLanguage);

  const emojiFontStack =
    '"Inter",-apple-system,BlinkMacSystemFont,"Segoe UI","Segoe UI Emoji","Segoe UI Symbol","Apple Color Emoji","Noto Color Emoji","Roboto","Oxygen","Ubuntu","Cantarell","Fira Sans","Droid Sans","Helvetica Neue",sans-serif';

  const dropdownStyles = {
    position: "relative",
    display: "inline-block",
    minWidth: deviceType === 'desktop' ? 80 : 0,
  };

  const buttonStyles = {
    background: "none",
    border: `1px solid ${theme.border}`,
    borderRadius: "6px",
    padding: deviceType === 'desktop' ? "16px 20px" : deviceType === 'tablet' ? "12px 16px" : window.innerWidth <= 400 ? "6px 8px" : "8px 12px",
    color: theme.text,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: deviceType === 'desktop' ? 6 : deviceType === 'tablet' ? 5 : 4,
    fontSize: deviceType === 'desktop' ? "14px" : deviceType === 'tablet' ? "14px" : window.innerWidth <= 400 ? "13px" : "14px",
    fontFamily: emojiFontStack,
    transition: "all 0.2s",
    minWidth: deviceType === 'desktop' ? 80 : 0,
    justifyContent: "space-between",
  };

  const dropdownContentStyles = {
    display: isOpen ? "block" : "none",
    position: "absolute",
    right: "0",
    top: "100%",
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    zIndex: 1000,
    minWidth: isMobile
      ? window.innerWidth <= 400
        ? 140
        : 160
      : 180,
    maxWidth: "220px",
    marginTop: "4px",
  };

  const languageItemStyles = {
    padding: isMobile ? "6px 8px" : "8px 12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: isMobile ? 6 : 8,
    fontSize: isMobile ? (window.innerWidth <= 400 ? "13px" : "14px") : "14px",
    color: theme.text,
    fontFamily: emojiFontStack,
    transition: "background-color 0.2s",
    borderRadius: "4px",
    margin: "2px",
    whiteSpace: "nowrap",
  };

  const handleLanguageChange = async (langCode) => {
    try {
      if (token) {
        await changeLanguage(langCode);
      } else {
        changeLanguageLocal(langCode);
      }
      setIsOpen(false);
    } catch (error) {
      setIsOpen(false);
    }
  };

  return (
    <>
      <style>
        {`
          .language-selector-btn:hover {
            background-color: ${theme.isDark ? "#2a2a2a" : "#f5f5f5"} !important;
            border-color: ${theme.primary} !important;
          }
          .language-item:hover {
            background-color: ${theme.isDark ? "#2a2a2a" : "#f5f5f5"} !important;
          }
          .language-item.active {
            background-color: ${theme.primary} !important;
            color: white !important;
          }
        `}
      </style>
      <div style={dropdownStyles}>
        <button
          className="language-selector-btn"
          style={buttonStyles}
          onClick={() => setIsOpen(!isOpen)}
        >
          {currentLang && (
            <img
              src={currentLang.iconUrl}
              alt={`${currentLang.name} flag`}
              width={isMobile ? 20 : 24}
              height={isMobile ? 14 : 16}
              style={{ display: "block", borderRadius: 3 }}
              loading="lazy"
              draggable="false"
              referrerPolicy="no-referrer"
            />
          )}
          {/* Hide code on extra small screens */}
          {!(isMobile && window.innerWidth <= 400) && (
            <span>{currentLang?.code.toUpperCase()}</span>
          )}
          <span style={{ fontSize: isMobile ? 9 : 10 }}>▼</span>
        </button>

        <div style={dropdownContentStyles}>
          {languages.map((language) => (
            <div
              key={language.code}
              className={`language-item ${currentLanguage === language.code ? "active" : ""}`}
              style={languageItemStyles}
              onClick={() => handleLanguageChange(language.code)}
            >
              <img
                src={language.iconUrl}
                alt={`${language.name} flag`}
                width={isMobile ? 20 : 24}
                height={isMobile ? 14 : 16}
                style={{ display: "block", borderRadius: 3 }}
                loading="lazy"
                draggable="false"
                referrerPolicy="no-referrer"
              />
              <span>{language.name}</span>
            </div>
          ))}
        </div>
      </div>

      {isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

// Theme Switch Component
const ThemeSwitch = () => {
  const { theme, isDarkMode, toggleTheme } = useTheme();
  const isMobile = useIsMobile();
  const deviceType = useDeviceType();

  const toggleSwitchStyles = (isOn) => ({
    position: "relative",
    width: deviceType === 'desktop' ? "80px" : deviceType === 'tablet' ? "70px" : window.innerWidth <= 400 ? "44px" : "60px",
    height: deviceType === 'desktop' ? "45px" : deviceType === 'tablet' ? "40px" : window.innerWidth <= 400 ? "28px" : "36px",
    borderRadius: "18px",
    backgroundColor: isOn ? theme.background : theme.border,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: deviceType === 'desktop' ? "10px 13px" : deviceType === 'tablet' ? "8px 11px" : window.innerWidth <= 400 ? "4px 6px" : "7px 10px",
  });

  const toggleKnobStyles = (isOn) => ({
    position: "absolute",
    top: deviceType === 'desktop' ? "6px" : deviceType === 'tablet' ? "5px" : window.innerWidth <= 400 ? "3px" : "5px",
    left: isOn
      ? deviceType === 'desktop' ? "43px" : deviceType === 'tablet' ? "38px" : window.innerWidth <= 400 ? "23px" : "33px"
      : deviceType === 'desktop' ? "5px" : deviceType === 'tablet' ? "5px" : window.innerWidth <= 400 ? "3px" : "5px",
    width: deviceType === 'desktop' ? "32px" : deviceType === 'tablet' ? "28px" : window.innerWidth <= 400 ? "16px" : "24px",
    height: deviceType === 'desktop' ? "32px" : deviceType === 'tablet' ? "28px" : window.innerWidth <= 400 ? "16px" : "24px",
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
    transition: "left 0.3s ease",
    zIndex: 2,
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
  });

  const iconStyles = {
    fontSize: deviceType === 'desktop' ? 20 : deviceType === 'tablet' ? 18 : window.innerWidth <= 400 ? 13 : 16,
    zIndex: 1,
    color: isDarkMode ? "#FFFFFF" : theme.text,
    fontWeight: "bold",
    lineHeight: "1",
  };

  return (
    <div style={toggleSwitchStyles(isDarkMode)} onClick={toggleTheme}>
      <span style={iconStyles}>☀</span>
      <span style={{ ...iconStyles, marginRight: deviceType === 'desktop' ? 6 : deviceType === 'tablet' ? 4 : 2 }}>☾</span>
      <div style={toggleKnobStyles(isDarkMode)} />
    </div>
  );
};

// Auth Navigator Component
const AuthNavigator = () => {
  const { isAuthenticated, loading } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to chat if already authenticated, but allow access to auth pages
  React.useEffect(() => {
    if (
      !loading &&
      isAuthenticated &&
      !["/forgot-password", "/verify-email", "/reset-password"].includes(location.pathname)
    ) {
      navigate("/chat");
    }
  }, [isAuthenticated, loading, navigate, location.pathname]);

  const authContainerStyles = {
    position: "relative",
    height: "100vh",
    backgroundColor: theme.background,
  };

  const languageSelectorWrapperStyles = {
    position: "absolute",
    top: "20px",
    right: "20px",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    gap: "12px",
  };

  return (
    <div style={authContainerStyles}>
      <div style={languageSelectorWrapperStyles}>
        <LanguageSelector />
      </div>
      <Outlet />
    </div>
  );
};

// RequireAuth component to protect routes
const RequireAuth = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    // Don't redirect to login for shared routes - they are publicly accessible
    if (!loading && !isAuthenticated && !location.pathname.startsWith("/shared")) {
      navigate("/login");
    }
  }, [isAuthenticated, loading, navigate, location.pathname]);

  if (loading) return <Loading fullScreen />;

  // For shared routes, show standalone shared view if not authenticated
  if (!isAuthenticated && location.pathname.startsWith("/shared")) {
    return <SharedChatScreen />;
  }

  return isAuthenticated ? children : null;
};

// Main Routes component
const MainRoutes = ({ chatData, sidebarOpen, setSidebarOpen }) => (
  <Routes>
    <Route path="/chat" element={<Navigate to="/chat/new" replace />} />
    <Route
      path="/chat/:chatId"
      element={
        <ChatScreen
          chatData={chatData}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />
      }
    />
    <Route
      path="/shared/:shareId"
      element={
        <ChatScreen
          chatData={chatData}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />
      }
    />
    <Route path="/archive/:chatId" element={<ChatScreen readOnly={true} />} />
    <Route path="/profile" element={<ProfileScreen />} />
    <Route path="/settings" element={<SettingsScreen />} />
    <Route path="/faq" element={<FAQScreen />} />
    <Route path="/blog" element={<BlogScreen />} />
    <Route path="/archive" element={<ArchiveScreen />} />
    <Route path="/subscription" element={<SubscriptionScreen />} />
    <Route path="*" element={<Navigate to="/chat/new" replace />} />
  </Routes>
);

// Main Navigator Component
const MainNavigator = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [chatData, setChatData] = useState(null);
  const isMobile = useIsMobile();
  const deviceType = useDeviceType();

  // На десктопі та планшетах сайдбар завжди відкритий, тільки на мобільних можна приховувати
  const effectiveSidebarOpen = deviceType === 'mobile' ? sidebarOpen : true;

  const { theme } = useTheme();
  const { t } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();

  // If we're on the forgot-password route, render it directly
  // if (location.pathname === "/forgot-password") {
  //   return <ForgotPasswordScreen />;
  // }

  // Get current active tab from URL pathname
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.startsWith("/shared")) return "chat";
    if (path.startsWith("/chat")) return "chat";
    if (path.startsWith("/profile")) return "profile";
    if (path.startsWith("/settings")) return "settings";
    if (path.startsWith("/faq")) return "faq";
    if (path.startsWith("/blog")) return "blog";
    if (path.startsWith("/archive")) return "archive";
    if (path.startsWith("/subscription")) return "subscription";
    return "chat";
  };

  const handleNavigation = (tab, data = null) => {
    if (data) {
      setChatData(data);
    }
    navigate(`/${tab}`);
  };

  const getScreenTitle = () => {
    const activeTab = getActiveTab();
    switch (activeTab) {
      case "chat":
        return t("chat");
      case "faq":
        return t("faq");
      case "profile":
        return t("profile");
      case "settings":
        return t("settings");
      case "blog":
        return t("blog");
      case "archive":
        return t("archive");
      case "subscription":
        return t("subscription");
      default:
        return "Chat App";
    }
  };

  const containerStyles = {
    display: "flex",
    height: "100vh",
    backgroundColor: theme.background,
  };

  const mainContentStyles = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    marginLeft: effectiveSidebarOpen ? (deviceType === 'tablet' ? "260px" : "280px") : "0px",
    transition: "margin-left 0.3s ease",
    width: effectiveSidebarOpen ? (deviceType === 'tablet' ? "calc(100vw - 260px)" : "calc(100vw - 280px)") : "100vw",
    maxWidth: effectiveSidebarOpen ? (deviceType === 'tablet' ? "calc(100vw - 260px)" : "calc(100vw - 280px)") : "100vw",
  };

  const hamburgerButtonStyles = {
    background: "none",
    border: "none",
    color: theme.text,
    fontSize: isMobile ? 22 : 25,
    cursor: "pointer",
    padding: isMobile ? 8 : 15,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s",
  };

  const activeTab = getActiveTab();
  const contentStyles = {
    flex: 1,
    overflow: activeTab === "subscription" && isMobile ? "auto" : "hidden",
    WebkitOverflowScrolling: activeTab === "subscription" && isMobile ? "touch" : "auto",
  };

  const languageSelectorWrapperStyles = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  };

  return (
    <>
      <style>
        {`
          .hamburger-btn:hover {
            background-color: ${theme.isDark ? "#2a2a2a" : "#f0f0f0"} !important;
          }
        `}
      </style>
      <div style={containerStyles}>
        <Sidebar
          isOpen={effectiveSidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          activeTab={getActiveTab()}
          onNavigate={handleNavigation}
        />

        <div style={mainContentStyles}>
          {/* Use Header component for all screens except chat and subscription */}
          {getActiveTab() !== "chat" && getActiveTab() !== "subscription" && (
            <Header
              title={getScreenTitle()}
              leftComponent={
                isMobile ? (
                  <button
                    className="hamburger-btn"
                    style={hamburgerButtonStyles}
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                  >
                    ☰
                  </button>
                ) : null
              }
              rightComponent={
                <div style={languageSelectorWrapperStyles}>
                  <ThemeSwitch />
                  <LanguageSelector />
                </div>
              }
            />
          )}

          <div style={contentStyles}>
            <MainRoutes
              chatData={chatData}
              sidebarOpen={effectiveSidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />
          </div>
        </div>
      </div>
    </>
  );
};

// Root Navigator Component
const RootNavigator = () => {
  const { isAuthenticated, loading } = useAuth();
  const { theme } = useTheme();
  const { t } = useLocalization();

  // Set theme variables for CSS - хук должен быть вызван всегда
  React.useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--primary-color", theme.primary);
    root.style.setProperty("--background-color", theme.background);
    root.style.setProperty("--surface-color", theme.surface);
    root.style.setProperty("--text-color", theme.text);
    root.style.setProperty("--border-color", theme.border);
  }, [theme]);

  // Request push notification permission when authenticated
  React.useEffect(() => {
    if (isAuthenticated && !loading) {
      PushNotificationService.init();
    }
  }, [isAuthenticated, loading]);

  if (loading) {
    return <Loading fullScreen text={t("loading")} />;
  }

  return isAuthenticated ? (
    <ChatProvider>
      <MainNavigator />
    </ChatProvider>
  ) : (
    <AuthNavigator />
  );
};

export const useDeviceType = () => {
  const [deviceType, setDeviceType] = React.useState(() => {
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const width = window.innerWidth;
    
    // Покращена логіка визначення iPad
    const isIPad = /iPad/i.test(navigator.userAgent) || 
                   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
                   (/Macintosh/i.test(navigator.userAgent) && isTouchDevice) ||
                   // Додаткова перевірка для сучасних iPad
                   (isTouchDevice && width >= 768 && width <= 1366);
    
    const isAndroidTablet = /Android/i.test(navigator.userAgent) && !/Mobile/i.test(navigator.userAgent);
    const isMobilePhone = /iPhone|Android.*Mobile|BlackBerry|Opera Mini|IEMobile|Windows Phone/i.test(navigator.userAgent);
    
    // Спрощена логіка: якщо це сенсорний пристрій з великим екраном - це планшет
    if (isMobilePhone && width <= 768) return 'mobile';
    if (isIPad || isAndroidTablet) return 'tablet';
    if (isTouchDevice && width >= 768) return 'tablet'; // Ключова зміна!
    if (width <= 768) return 'mobile';
    return 'desktop';
  });

  React.useEffect(() => {
    const handleResize = () => {
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const width = window.innerWidth;
      
      const isIPad = /iPad/i.test(navigator.userAgent) || 
                     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
                     (/Macintosh/i.test(navigator.userAgent) && isTouchDevice) ||
                     (isTouchDevice && width >= 768 && width <= 1366);
      
      const isAndroidTablet = /Android/i.test(navigator.userAgent) && !/Mobile/i.test(navigator.userAgent);
      const isMobilePhone = /iPhone|Android.*Mobile|BlackBerry|Opera Mini|IEMobile|Windows Phone/i.test(navigator.userAgent);
      
      let newDeviceType;
      if (isMobilePhone && width <= 768) newDeviceType = 'mobile';
      else if (isIPad || isAndroidTablet) newDeviceType = 'tablet';
      else if (isTouchDevice && width >= 768) newDeviceType = 'tablet'; // Ключова зміна!
      else if (width <= 768) newDeviceType = 'mobile';
      else newDeviceType = 'desktop';
      
      setDeviceType(newDeviceType);
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  return deviceType;
};

// Add a hook to detect mobile and tablet devices (for backward compatibility)
export const useIsMobile = () => {
  const deviceType = useDeviceType();
  return deviceType === 'mobile' || deviceType === 'tablet';
};

// Session Expired Banner
const SessionExpiredBanner = () => {
  const { sessionExpired, clearSessionExpired } = useAuth();
  const { t } = useLocalization();
  const { theme } = useTheme();

  React.useEffect(() => {
    if (!sessionExpired) return;
    const timer = setTimeout(clearSessionExpired, 5000);
    return () => clearTimeout(timer);
  }, [sessionExpired, clearSessionExpired]);

  if (!sessionExpired) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        padding: "14px 20px",
        backgroundColor: "#d32f2f",
        color: "#fff",
        fontSize: "14px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      }}
    >
      <span>
        <strong>{t("sessionExpired")}</strong>
        {" — "}
        {t("sessionExpiredDesc")}
      </span>
      <button
        onClick={clearSessionExpired}
        style={{
          background: "none",
          border: "none",
          color: "#fff",
          fontSize: "18px",
          cursor: "pointer",
          lineHeight: 1,
          padding: "0 4px",
          opacity: 0.8,
        }}
        aria-label="Close"
      >
        ×
      </button>
    </div>
  );
};

// Компонент для відстеження навігації всередині Router
const AppWithTracking = () => {
  usePageTracking();
  useUtmParams();

  return (
    <>
      <SessionExpiredBanner />
      <Routes>
        {/* Auth routes */}
        <Route path="/" element={<AuthNavigator />}>
          <Route index element={<Navigate to="/login" replace />} />
          <Route path="login" element={<LoginScreen />} />
          <Route path="register" element={<RegisterScreen />} />
          <Route path="verify-email" element={<EmailVerificationScreen />} />
          <Route path="forgot-password" element={<ForgotPasswordScreen />} />
          <Route path="reset-password" element={<ResetPasswordScreen />} />
        </Route>

        {/* Protected routes (shared routes handled inside RequireAuth) */}
        <Route
          path="/*"
          element={
            <RequireAuth>
              <RootNavigator />
            </RequireAuth>
          }
        />
      </Routes>
    </>
  );
};

// Main App Component
const App = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <LocalizationProvider>
          <BrowserRouter>
            <AppWithTracking />
          </BrowserRouter>
        </LocalizationProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
